import * as nsVue from 'vue'
import { Vue, Component, Inject, Model, Prop, Watch } from 'vue-property-decorator'
import App from '../App'
import { Node } from "../models";
import hyper from "../hyper";
import { liset, sleep } from "../utils";

export interface SelectNodeResult
{
  portal: string;
  nodeId: number;
  active: boolean;
}

type PortalData = {
  key: string;
  label: string;
  tracks: number[];
  root: Node | null;
  error: string | null;
  loading: boolean;
}

function isIdInTree(node: Node, id: number) {
  if (node.id === id)
    return true
  for (let child of node.children) {
    if (isIdInTree(child, id))
      return true
  }
}

function scrollIntoView(container: HTMLElement, target: HTMLElement) {
  let h0 = container.clientHeight
  let h1 = target.clientHeight
  let stop = container.scrollTop
  let top = target.offsetTop
  let vtop = top - stop
  const H = 1
  if (vtop < H) {
    container.scrollTop -= H - vtop
    return
  }
  if (vtop + h1 > h0 - H) {
    if (h1 > h0) {
      container.scrollTop += vtop - H 
    }
    else {
      container.scrollTop += vtop + h1 - h0 + H
    }
  }
}

let TreeItem: nsVue.FunctionalComponentOptions = {
  functional: true,
  props: ['root', 'tracks', 'selected', 'changeSelected', 'filter'],
  render(createElement, ctx) {
    let { e, ul, li, div, span, i } = hyper(createElement)
    let root = ctx.props.root as Node
    let tracks = ctx.props.tracks as number[]
    let selected = ctx.props.selected as number | null
    let changeSelected = ctx.props.changeSelected as (id: number | null) => void
    let filter = ctx.props.filter as (node: Node) => boolean | undefined
    let cid = parseInt(ctx.parent.$route.params.nodeId)
    function c(node: Node, level: number) {
      let active$ = node.id === selected
      let disabled$ = filter && filter(node) === false
      let show = level===0 || tracks.indexOf(node.id)!==-1
      let toggle = function (event: Event) {
        if (liset.toggle(tracks, node.id)) {
          Vue.nextTick(() => {
            let container = ctx.parent.$el.querySelector('div.panes')
            let target = ctx.parent.$el.querySelector(`li[data-id="${node.id}"]`)
            if (container && target) {
              scrollIntoView(container as HTMLElement, target as HTMLElement)
            }
          })
        }
        else {
          Vue.nextTick(() => {
            let container = ctx.parent.$el.querySelector('div.panes')
            let target = ctx.parent.$el.querySelector(`li[data-id="${node.id}"]>div`)
            if (container && target) {
              scrollIntoView(container as HTMLElement, target as HTMLElement)
            }
          })
        }
        if (selected && isIdInTree(node, selected))
          changeSelected(disabled$ ? null : node.id)
        event.stopPropagation()
        event.preventDefault()
      }
      let select = function (event: Event) {
        if (disabled$ || selected === node.id)
          return
        changeSelected(node.id)
        Vue.nextTick(() => {
          let container = ctx.parent.$el.querySelector('div.panes')
          let target = ctx.parent.$el.querySelector(`li[data-id="${node.id}"]>div`)
          if (container && target) {
            scrollIntoView(container as HTMLElement, target as HTMLElement)
          }
        })
        event.stopPropagation()
        event.preventDefault()
      }
      let ownChild = node.children.length !== 0
      let showIcon = level !== 0 && ownChild
      return li({'data-id': node.id}, [
        div({active$, disabled$, $click: select}, [
          span({class: 'indent'}, '\t'.repeat(level)),
          span({class: 'label'}, [
            !showIcon?e: i({$click: toggle}, show?'[-]':'[+]'),
            node.name
          ])
        ]),
        !ownChild?e: ul({show}, node.children.map(n=>c(n, level+1)))
      ])
    }
    return c(root, 0)
  }
}

@Component({
  components: {TreeItem}
})
export default class SelectNode extends Vue {
  @Prop() filter?: (node: Node) => boolean

  @Prop() commit: (data: any) => void

  @Prop() cancel: () => void

  selected: number | null = null

  portals: PortalData[] = []

  current = -1

  active: boolean = false

  created() {
    let app = this.$root.$children[0] as App
    let routeKey = this.$route.params.portalKey
    let current = 0
    app.portals.forEach((p, i) => {
      let {key, label} = p
      this.portals.push({key, label, root: null, error: null, tracks: [], loading: false})
      if (p.key === routeKey)
        current = i
    })
    this.expand(current)
  }

  changeSelected(id: number | null) {
    this.selected = id
  }

  async expand(index: number) {
    if (this.current === index)
      return
    this.current = index
    this.selected = null
    let app = this.$root.$children[0] as App
    let portal = this.portals[index]
    if (!portal.root && !portal.error) {
      await sleep(0)
      portal.loading = true
      try {
        portal.root = await app.getPortalRoot(portal.key)
        portal.loading = false
      }
      catch (e) {
        portal.error = (e as Error).message
        portal.loading = false
      }
    }
  }

  submit() {
    if (this.selected) {
      let active = this.active
      let result: SelectNodeResult = {
        portal: this.portals[this.current].key,
        nodeId: this.selected,
        active
      }
      this.commit(result)
    }
  }
}
